import React, { useEffect, useState } from "react";
import {
  FiArchive,
  FiBox,
  FiCheck,
  FiEdit3,
  FiInfo,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import MenuLateral from "../../Components/MenuLateral";
import api from "../../services/api";
import { showApiError } from "../../services/showApiError";
import Swal from "sweetalert2";
import "./index.css";

const CATEGORIES = [
  { value: "album", label: "Álbum" },
  { value: "digital_album", label: "Álbum digital" },
  { value: "impact_album", label: "Álbum de impacto" },
  { value: "link", label: "Link" },
  { value: "single_photo", label: "Foto avulsa" },
  { value: "other", label: "Outros" },
];

const emptyProduct = {
  id: "",
  name: "",
  category: "album",
  description: "",
  numberOfPhotos: "",
  active: true,
};

function getCategoryLabel(category) {
  return CATEGORIES.find((item) => item.value === category)?.label || category;
}

function CadProduto() {
  const [product, setProduct] = useState(emptyProduct);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get("/products", {
        params: {
          search: search || undefined,
          includeInactive,
          limit: 100,
        },
      });
      setProducts(response.data.products || []);
    } catch (error) {
      showApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
    // A busca é disparada explicitamente para não consultar a cada caractere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const resetForm = () => setProduct(emptyProduct);

  const updateProduct = (field, value) => {
    setProduct((current) => ({ ...current, [field]: value }));
  };

  const saveProduct = async (event) => {
    event.preventDefault();

    if (!product.name.trim()) {
      Swal.fire("Nome obrigatório", "Informe o nome do produto.", "warning");
      return;
    }

    const numberOfPhotos = Number(product.numberOfPhotos);
    if (!Number.isInteger(numberOfPhotos) || numberOfPhotos < 0) {
      Swal.fire(
        "Quantidade inválida",
        "Informe uma quantidade inteira de fotos igual ou maior que zero.",
        "warning",
      );
      return;
    }

    const payload = {
      name: product.name.trim(),
      category: product.category,
      description: product.description.trim() || undefined,
      numberOfPhotos,
    };

    setLoading(true);
    try {
      if (product.id) {
        await api.patch(`/products/${product.id}`, {
          ...payload,
          description: product.description.trim(),
          active: product.active,
        });
        await Swal.fire("Atualizado!", "Produto atualizado com sucesso.", "success");
      } else {
        await api.post("/products", payload);
        await Swal.fire("Cadastrado!", "Produto criado com sucesso.", "success");
      }
      resetForm();
      await loadProducts();
    } catch (error) {
      showApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const selectProduct = (selectedProduct) => {
    setProduct({
      id: selectedProduct.id,
      name: selectedProduct.name || "",
      category: selectedProduct.category || "album",
      description: selectedProduct.description || "",
      numberOfPhotos: String(selectedProduct.numberOfPhotos ?? ""),
      active: selectedProduct.active !== false,
    });

    const form = document.querySelector(".form-cad");
    if (form) form.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const deactivateProduct = async (selectedProduct) => {
    const confirmation = await Swal.fire({
      title: "Inativar produto?",
      text: `O produto ${selectedProduct.name} ficará indisponível para novos pedidos.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Inativar",
      cancelButtonText: "Cancelar",
    });
    if (!confirmation.isConfirmed) return;

    setLoading(true);
    try {
      await api.delete(`/products/${selectedProduct.id}`);
      await Swal.fire("Inativado", "Produto inativado com sucesso.", "success");
      if (product.id === selectedProduct.id) resetForm();
      await loadProducts();
    } catch (error) {
      showApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const restoreProduct = async (selectedProduct) => {
    setLoading(true);
    try {
      await api.post(`/products/${selectedProduct.id}/restore`);
      await Swal.fire("Restaurado", "Produto restaurado com sucesso.", "success");
      await loadProducts();
    } catch (error) {
      showApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const activeProductsCount = products.filter((listedProduct) => listedProduct.active).length;
  const inactiveProductsCount = products.length - activeProductsCount;
  const isEditing = Boolean(product.id);

  return (
    <div className="pagina-cad pagina-produtos">
      <MenuLateral />

      <main className="conteudo-cad produtos-conteudo">
        <header className="produtos-header">
          <div>
            <span className="produtos-eyebrow">Operação comercial</span>
            <h1>Catálogo de produtos</h1>
            <p>
              Organize os itens usados nos pedidos e defina quantas fotos cada produto precisa.
            </p>
          </div>
          <div className="produtos-header-note" aria-label="Orientação do catálogo">
            <span className="produtos-header-note-icon"><FiBox aria-hidden="true" /></span>
            <span>
              <strong>Cadastro local</strong>
              <small>Dados essenciais para o pedido</small>
            </span>
          </div>
        </header>

        <div className="produtos-layout">
          <form className="form-cad produtos-form" onSubmit={saveProduct}>
            <div className="produtos-panel-heading">
              <div className="produtos-panel-icon"><FiPlus aria-hidden="true" /></div>
              <div>
                <span className="produtos-panel-kicker">{isEditing ? "Edição de cadastro" : "Novo cadastro"}</span>
                <h2>{isEditing ? "Atualizar produto" : "Adicionar produto"}</h2>
                <p>Preencha somente as informações que orientam a montagem do pedido.</p>
              </div>
            </div>

            <div className="produtos-form-fields">
              <label className="produtos-field produtos-field-wide">
                Nome do produto
                <input
                  aria-label="Nome do produto"
                  placeholder="Ex.: Álbum de formatura"
                  value={product.name}
                  onChange={(event) => updateProduct("name", event.target.value)}
                  disabled={loading}
                  required
                />
              </label>

              <label className="produtos-field">
                Categoria
                <select
                  aria-label="Categoria"
                  value={product.category}
                  onChange={(event) => updateProduct("category", event.target.value)}
                  disabled={loading}
                >
                  {CATEGORIES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="produtos-field">
                Quantidade obrigatória de fotos
                <input
                  aria-label="Quantidade obrigatória de fotos"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={product.numberOfPhotos}
                  onChange={(event) => updateProduct("numberOfPhotos", event.target.value)}
                  disabled={loading}
                  required
                />
                <small>Use 0 quando o item não exige seleção de fotos.</small>
              </label>

              <label className="produtos-field produtos-field-wide">
                Descrição
                <textarea
                  aria-label="Descrição"
                  placeholder="Uma nota curta para identificar este produto na operação."
                  value={product.description}
                  onChange={(event) => updateProduct("description", event.target.value)}
                  disabled={loading}
                />
              </label>
            </div>

            <div className="produtos-form-note">
              <FiInfo aria-hidden="true" />
              <span>A quantidade de fotos será usada como referência em cada item do pedido.</span>
            </div>

            {isEditing && (
              <label className="produtos-active-toggle">
                <input
                  aria-label="Produto ativo"
                  type="checkbox"
                  checked={product.active}
                  onChange={(event) => updateProduct("active", event.target.checked)}
                  disabled={loading}
                />
                <span>
                  <strong>Produto ativo</strong>
                  <small>Disponível para novos pedidos</small>
                </span>
              </label>
            )}

            <div className="botoes produtos-form-actions">
              <button type="submit" className="produtos-button produtos-button-primary" disabled={loading}>
                {isEditing ? <FiCheck aria-hidden="true" /> : <FiPlus aria-hidden="true" />}
                {isEditing ? "Atualizar produto" : "Salvar produto"}
              </button>
              <button type="button" className="btn-limpar produtos-button produtos-button-secondary" onClick={resetForm} disabled={loading}>
                <FiX aria-hidden="true" />
                Limpar
              </button>
            </div>
          </form>

          <aside className="produtos-summary" aria-label="Resumo do catálogo">
            <div className="produtos-summary-heading">
              <span className="produtos-summary-icon"><FiArchive aria-hidden="true" /></span>
              <span>
                <span className="produtos-summary-eyebrow">Visão rápida</span>
                <strong>Catálogo atual</strong>
              </span>
            </div>
            <p className="produtos-summary-intro">Acompanhe o resultado da consulta enquanto organiza os cadastros.</p>
            <div className="produtos-summary-total">
              <strong>{products.length}</strong>
              <span>produto{products.length === 1 ? " exibido" : "s exibidos"}</span>
            </div>
            <div className="produtos-summary-stats">
              <div>
                <span>Ativos</span>
                <strong>{activeProductsCount}</strong>
              </div>
              <div>
                <span>Inativos</span>
                <strong>{inactiveProductsCount}</strong>
              </div>
            </div>
            <div className="produtos-summary-rule">
              <span className="produtos-summary-rule-dot" />
              <span>O cadastro fica disponível para pedidos enquanto estiver ativo.</span>
            </div>
          </aside>
        </div>

        <section className="produtos-list-section" aria-label="Lista de produtos">
          <div className="produtos-list-heading">
            <div>
              <span className="produtos-panel-kicker">Base operacional</span>
              <h2>Produtos cadastrados</h2>
              <p>Edite informações ou inative itens que não devem aparecer em novos pedidos.</p>
            </div>
            <span className="produtos-count-badge">{products.length} {products.length === 1 ? "item" : "itens"}</span>
          </div>

          <div className="produto-filtros produtos-toolbar">
            <label className="produtos-search-field">
              <span>Buscar produto</span>
              <div>
                <FiSearch aria-hidden="true" />
                <input
                  aria-label="Buscar produto"
                  placeholder="Nome do produto..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  disabled={loading}
                />
                {search && (
                  <button
                    type="button"
                    className="produtos-search-clear"
                    aria-label="Limpar busca do produto"
                    onClick={() => setSearch("")}
                    disabled={loading}
                  >
                    <FiX aria-hidden="true" />
                  </button>
                )}
              </div>
            </label>
            <button type="button" className="produtos-button produtos-button-secondary produtos-search-button" onClick={loadProducts} disabled={loading}>
              <FiSearch aria-hidden="true" />
              Buscar
            </button>
            <label className="produtos-inactive-toggle">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(event) => setIncludeInactive(event.target.checked)}
                disabled={loading}
              />
              <span>Incluir inativos</span>
            </label>
          </div>

          {loading && <p className="produtos-loading" role="status">Carregando catálogo...</p>}

          <div className="produtos-table-wrap">
            <table className="grid-cad produtos-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Categoria</th>
                  <th>Fotos obrigatórias</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {products.map((listedProduct) => (
                  <tr key={listedProduct.id}>
                    <td data-label="Nome">
                      <div className="produtos-name-cell">
                        <span className="produtos-row-icon"><FiBox aria-hidden="true" /></span>
                        <span>
                          <strong>{listedProduct.name}</strong>
                          {listedProduct.description && <small>{listedProduct.description}</small>}
                        </span>
                      </div>
                    </td>
                    <td data-label="Categoria"><span className="produtos-category-badge">{getCategoryLabel(listedProduct.category)}</span></td>
                    <td data-label="Fotos obrigatórias">
                      <span className="produtos-photo-count"><strong>{listedProduct.numberOfPhotos}</strong> fotos</span>
                    </td>
                    <td data-label="Status">
                      <span className={`produtos-status-badge ${listedProduct.active ? "is-active" : "is-inactive"}`}>
                        <span aria-hidden="true" />
                        {listedProduct.active ? "Ativo" : "Inativo"}
                      </span>
                    </td>
                    <td data-label="Ações">
                      <div className="produtos-row-actions">
                        <button type="button" className="produtos-row-button produtos-row-button-edit" onClick={() => selectProduct(listedProduct)} disabled={loading}>
                          <FiEdit3 aria-hidden="true" />
                          Editar
                        </button>
                        {listedProduct.active ? (
                          <button
                            type="button"
                            className="btn-excluir produtos-row-button produtos-row-button-danger"
                            onClick={() => deactivateProduct(listedProduct)}
                            disabled={loading}
                          >
                            <FiTrash2 aria-hidden="true" />
                            Inativar
                          </button>
                        ) : (
                          <button type="button" className="produtos-row-button produtos-row-button-restore" onClick={() => restoreProduct(listedProduct)} disabled={loading}>
                            <FiRefreshCw aria-hidden="true" />
                            Restaurar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && !loading && (
                  <tr>
                    <td className="produtos-empty-row" colSpan="5">
                      <span className="produtos-empty-icon"><FiBox aria-hidden="true" /></span>
                      <strong>Nenhum produto encontrado</strong>
                      <span>Ajuste a busca ou cadastre o primeiro produto acima.</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

export default CadProduto;
